import { Component, ElementRef, Input, ViewChild } from '@angular/core';
import { ActivatedRoute, ChildActivationEnd, Router } from '@angular/router';
import { CalendarView } from 'angular-calendar';
import dayjs from 'dayjs';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import { Subject, take, takeUntil } from 'rxjs';
import { ModalService } from 'src/app/project/components/modal/modal.service';
import { translate } from 'src/app/project/features/translate/translate';
import { TranslationService } from 'src/app/project/services/translation.service';
import { EIconPath } from 'src/app/project/shared/enums/icons.enum';
import { QuickReminderFilterService } from '../../filters/filters-api/reminders-search/quick-reminder-filter.service';
import { TReminderModalData } from '../../points/point-reminders/point-reminder-modal/point-reminder-modal.consts';
import { PointReminderModalService } from '../../points/point-reminders/point-reminder-modal/point-reminder-modal.service';
import { TReminder } from '../reminders.consts';
import { RemindersService } from '../reminders.service';
import { TReminderEvent } from './reminder-calendar-consts';

dayjs.extend(weekOfYear);

@Component({
  selector: 'pp-reminders-calendar',
  templateUrl: './reminders-calendar.component.html',
  styleUrl: './reminders-calendar.component.scss',
  standalone: false,
})
export class RemindersCalendarComponent {
  @ViewChild('calendar', { static: true }) calendar: ElementRef<HTMLElement>;
  @ViewChild('periodView', { read: ElementRef }) dayView: ElementRef;

  @Input() ppDataFetched = false;

  EIconPath = EIconPath;
  CalendarView = CalendarView;
  viewDate = new Date();
  calendarView = CalendarView.Month;
  events: TReminderEvent[] = [];
  locale = 'en';
  pointsFiltered = false;

  private destroy$ = new Subject<void>();

  constructor(
    private remindersService: RemindersService,
    private translationService: TranslationService,
    private route: ActivatedRoute,
    private router: Router,
    private modalService: ModalService,
    private pointReminderModalService: PointReminderModalService,
    private quickReminderFilterService: QuickReminderFilterService,
  ) {
    this.locale = this.translationService.getLocale();

    this.remindersService.clearData();

    this.remindersService
      .fetchReminders()
      .pipe(takeUntil(this.destroy$))
      .subscribe((reminders) => {
        this.setReminders(reminders);
      });

    this.remindersService.quickFetchReminders();

    this.remindersService.remindersChanged$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.setReminders(this.remindersService.getReminders());

      const filters = this.quickReminderFilterService.getFilters();

      const isDefault =
        filters.length === 3 &&
        filters[0].values.length === 3 &&
        filters[1].values.length === 3 &&
        filters[2].values.length === 2;

      this.pointsFiltered = !isDefault;
    });

    this.remindersService.quickFetchReminders();

    this.route.queryParams.subscribe((params) => {
      this.calendarView = params.mode;

      this.handleReminderUrlChange(params.reminderId);
    });

    this.router.events.subscribe((event) => {
      if (event instanceof ChildActivationEnd) {
        this.scrollToContentInView();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  ngAfterViewInit(): void {
    this.scrollToContentInView();
  }

  onViewChange(view: CalendarView): void {
    if (view !== CalendarView.Day) {
      this.calendar.nativeElement.parentElement.parentElement.scrollTop = 0;
    }

    this.scrollToContentInView();

    this.onViewRendered(view);
  }

  onViewDateChange(): void {
    this.onViewRendered(this.calendarView);
    this.scrollToContentInView();
  }

  scrollToContentInView(): void {
    setTimeout(() => {
      const container = this.dayView?.nativeElement as HTMLElement;
      const timeMarker = container?.querySelector('.cal-current-time-marker') as HTMLElement;

      if (container) {
        const target = timeMarker
          ? timeMarker
          : (container?.querySelector('.calendarEvent ') as HTMLElement);

        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          const targetHeight =
            ((dayjs().hour() * 60 + dayjs().minute()) / 1440) * container.scrollHeight;
          container.scrollTop = targetHeight - container.clientHeight / 2;
        }
      }
    });
  }

  private setReminders(reminders: TReminder[]): void {
    this.events = reminders.map((reminder) => ({
      title: reminder.name,
      start: new Date(reminder.timestampEpochMillis),
      reminder: reminder,
    }));
  }

  onViewRendered(view: CalendarView): void {
    setTimeout(() => {
      switch (view) {
        case CalendarView.Day:
          // TODO;
          break;
        case CalendarView.Week:
          this.onWeekViewRendered();
          break;
        case CalendarView.Month:
          this.onMonthViewRendered();
          break;
      }
    });
  }

  onWeekViewRendered(): void {
    const headerElement = document.querySelector('.cal-day-headers');
    const columns = document.querySelectorAll('.cal-time-events');

    const oldHeaderElement = headerElement?.querySelector('.remindersCalendarWeek__weekHeader');
    if (oldHeaderElement) {
      oldHeaderElement.remove();
    }

    const weekHeaderElement = document.createElement('div');
    weekHeaderElement.classList.add('remindersCalendarWeek__weekHeader');

    const weekHeaderElementInner = document.createElement('div');
    weekHeaderElementInner.classList.add('remindersCalendarWeek__weekHeaderInner');
    weekHeaderElement.appendChild(weekHeaderElementInner);

    const weekText = document.createTextNode(translate('week'));
    weekHeaderElementInner.appendChild(weekText);

    headerElement?.insertBefore(weekHeaderElement, headerElement.firstChild);

    const column = columns[0];

    const oldElement = column.querySelector('.remindersCalendarWeek__weekNumber');
    if (oldElement) {
      oldElement.remove();
    }

    const weekElement = document.createElement('div');
    weekElement.classList.add('remindersCalendarWeek__weekNumber');
    const weekElementInner = document.createElement('div');
    weekElementInner.classList.add('remindersCalendarWeek__weekNumberInner');
    const weekNumberText = document.createTextNode(dayjs(this.viewDate).week().toString());

    weekElement.appendChild(weekElementInner);
    weekElementInner.appendChild(weekNumberText);
    column.insertBefore(weekElement, column.firstChild);
  }

  onMonthViewRendered(): void {
    const rows = document.querySelectorAll('.cal-cell-row');

    rows.forEach((row, index) => {
      if (index === 0) {
        const oldElement = row.querySelector('.remindersCalendarMonth__weekNumber');

        if (oldElement) {
          oldElement.remove();
        }

        const weekElement = document.createElement('div');
        weekElement.classList.add('remindersCalendarMonth__weekNumber');
        weekElement.classList.add('remindersCalendarMonth__weekNumber--first');

        const weekText = document.createTextNode(translate('week'));
        weekElement.appendChild(weekText);

        row.insertBefore(weekElement, row.firstChild);
      } else {
        const oldElement = row.querySelector('.remindersCalendarMonth__weekNumber');

        if (oldElement) {
          oldElement.remove();
        }

        const weekElement = document.createElement('div');
        weekElement.classList.add('remindersCalendarMonth__weekNumber');

        if (index === rows.length - 1) {
          weekElement.classList.add('remindersCalendarMonth__weekNumber--last');
        }

        const firstCell = row.firstChild;
        const firstCellDate = +(firstCell.childNodes[1] as HTMLElement).getAttribute('data-day');
        const weekNumber = dayjs(firstCellDate).week();

        const weekText = document.createTextNode(weekNumber.toString());
        weekElement.appendChild(weekText);

        row.insertBefore(weekElement, row.firstChild);
      }
    });
  }

  private handleReminderUrlChange(reminderId: string): void {
    if (reminderId) {
      const reminder = this.remindersService
        .getReminders()
        .find((searchedReminder) => searchedReminder.reminderId === reminderId);

      if (reminder) {
        this.showReminderFromUrl(reminder);
      } else {
        this.remindersService.remindersChanged$.pipe(take(1)).subscribe((reminders) => {
          const reminderFromURL = reminders.find(
            (searchedReminder) => searchedReminder.reminderId === reminderId,
          );

          if (reminderFromURL) {
            this.showReminderFromUrl(reminderFromURL);
          }
        });
      }
    } else {
      this.modalService.hideModal();
    }
  }

  private showReminderFromUrl(reminder: TReminder): void {
    const modalData: TReminderModalData = {
      reminder,
    };

    this.modalService.hideModal().then(() => {
      this.pointReminderModalService.showReminderModal(modalData);
    });
  }
}
